import React, { useState, useEffect } from 'react';
import { SingleValue } from 'react-select';
import toast from 'react-hot-toast';

import { Item } from '@/types';

import {
  getItems4Company,
  useAddItem4AI,
  useDeleteItemfromAIModule,
} from '../api';
import {
  AIModuleEntity,
  Item4AIPayload,
  emptyItem4AI,
  Item4AI,
  Item4Company,
} from '../types';

type InputChange = React.ChangeEvent<HTMLInputElement>;
type SelectChange = React.ChangeEvent<HTMLSelectElement>;

export const useItemsUnderAICompany = (
  aiModuleSelected: AIModuleEntity,
  items: (Item | undefined)[] | undefined,
  handleClose: () => void
) => {
  const { id } = aiModuleSelected;

  const [formVisible, setFormVisible] = useState(false);
  const [payload, setPayload] = useState<Item4AIPayload>(emptyItem4AI);
  const [showExpandedFields, setShowExpandedFields] = useState(false);
  const [itemRows, setItemRows] = useState<Item4Company[]>([]);

  const showForm = () => setFormVisible(true);
  const hideForm = () => setFormVisible(false);

  const addNewItemQuery = useAddItem4AI({
    onSuccess: async () => {
      const company = parseInt(payload.company_id);
      const { data: rows } = await getItems4Company(id, company);
      // console.log(`>>> got updated rows:`);
      // console.log(rows);
      rows && setItemRows(rows);
      hideForm();
    },
  });
  const { isLoading } = addNewItemQuery;

  const deleteItemQuery = useDeleteItemfromAIModule({
    onSuccess: async () => {
      const company = parseInt(payload.company_id);
      const { data: rows } = await getItems4Company(id, company);
      // console.log(`>>> got updated rows:`);
      // console.log(rows);
      rows && setItemRows(rows);
    },
  });

  const { isLoading: isDeleting } = deleteItemQuery;

  const itemToSelectOpt = (c: Item) => ({
    label: c.description,
    value: c.id,
  });

  const itemOptions = items?.map((it) =>
    it ? itemToSelectOpt(it) : { label: 'Unknown', value: 'UNKNOWN' }
  );

  /**
   * == STEP - 1: query items being filtered out
   * @param event
   */
  const selectCompanyToQuery = async (event: SelectChange) => {
    const { name, value } = event.target;
    // console.log(`>>> to query items from company:`);
    // console.log(name, value);
    // uddate `company_id` field:
    setPayload({ ...payload, company_id: value });
    const { data: rows } = await getItems4Company(id, parseInt(value));
    // console.log(rows);
    // TODO: list item in the table...
    rows && setItemRows(rows);
  };

  /**
   * == STEP - 2: save new one ==
   * @param event
   */
  const handleSubmitToEditAIModule = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const omits = {
      ai_module_id: id,
      company_id: parseInt(payload.company_id),
      item_id: payload.item_id,
      action: payload.action,
    };

    const payloadToSave: Item4AI = {
      ...omits,
      expanded_item_id: payload.expanded_item,
      expansion_length_formula: payload.formula,
      formula_correction_factor: payload.correction_factor,
    };
    const isOmmit = payload.action == 'OMMIT';
    const simplifiedPayload = isOmmit ? omits : payloadToSave;
    // ! do use `try ... await ... catch` to call `mutateAsync` to enable
    // ! `onError` inside of mutation get called!
    // ! @2025/05/14
    try {
      await addNewItemQuery.mutateAsync({ payload: simplifiedPayload });
    } catch (error) {
      // console.error(error);
    }
  };

  const handleCompanySelect = (event: SelectChange) => {
    const { name, value } = event.target;
    // uddate `company_id` field:
    setPayload({ ...payload, company_id: value });
  };

  const handleAlgorithmSelect = (event: SelectChange) => {
    const { name, value } = event.target;
    setPayload({ ...payload, formula: value });
  };

  const handleActionSelect = (event: SelectChange) => {
    const { name, value } = event.target;
    setPayload({ ...payload, action: value });
    if (value === 'EXPAND') {
      // show optional inputs ...
      setShowExpandedFields(true);
    } else {
      setShowExpandedFields(false);
    }
  };

  const handleCorrectionFactorInput = (event: InputChange) => {
    const { name, value } = event.target;
    setPayload({ ...payload, correction_factor: parseFloat(value) });
  };

  const handleItemSelect = (
    item: SingleValue<{ label: string; value: string }>
  ) => {
    if (!item) return;
    setPayload({ ...payload, item_id: item.value });
  };

  const handleExpandedItemInput = (
    item: SingleValue<{ label: string; value: string }>
  ) => {
    if (!item) return;
    // ! check select item different from above one!!
    // @2025/05/09
    setPayload({ ...payload, expanded_item: item.value });
  };

  const handleItemRowDelete = async (row: Item4Company) => {
    // console.log(item);
    const mapper = (r: Item4Company) =>
      r === row ? { ...r, deleting: true } : r;
    // mark deletion status
    setItemRows(itemRows.map(mapper));
    const vars = {
      aid: id,
      cid: Number(payload.company_id),
      item: row.item_id,
      act: row.action,
    };
    try {
      await deleteItemQuery.mutateAsync(vars);
    } catch (error) {
      //
    }
  };

  /**
   * do some reset work at close!
   * @2025/05/16
   */
  const handleRestoreAndClose = () => {
    handleClose();
    setPayload(emptyItem4AI);
    setItemRows([]);
    setShowExpandedFields(false);
  };

  return {
    isLoading,
    isDeleting,
    formVisible,
    payload,
    itemRows,
    itemOptions,
    showExpandedFields,
    selectCompanyToQuery,
    showForm,
    hideForm,
    handleSubmitToEditAIModule,
    handleCompanySelect,
    handleItemSelect,
    handleActionSelect,
    handleExpandedItemInput,
    handleAlgorithmSelect,
    handleCorrectionFactorInput,
    handleItemRowDelete,
    handleRestoreAndClose,
  };
};
